import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useCallback, useRef, useState } from "react";

import {
  MerryCardMenu,
  type MerryCardMenuAction,
  type MerryCardMenuCatalog,
} from "@/shared/ui/MerryCardMenu";
import {
  getMerrySystemMenuFlyoutTop,
  getMerrySystemMenuPageFigmaNodeId,
  isMerrySystemMenuSubmenuView,
  type MerryCardMenuView,
  type MerrySystemMenuSubmenuView,
} from "@/shared/ui/merryCardMenuVariants";

gsap.registerPlugin(useGSAP);

export interface MerrySystemMenuProps {
  "aria-label"?: string;
  "data-testid"?: string;
  catalog: MerryCardMenuCatalog;
  className?: string;
  currency?: string;
  defaultCurrency?: string;
  defaultLanguage?: string;
  language?: string;
  onAction?: (action: MerryCardMenuAction, view: MerryCardMenuView) => void;
  onCurrencyChange?: (currency: string) => void;
  onLanguageChange?: (language: string) => void;
  onSubmenuChange?: (submenu: MerrySystemMenuSubmenuView | null) => void;
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function MerrySystemMenu({
  "aria-label": ariaLabel = "系统菜单",
  "data-testid": testId,
  catalog,
  className,
  currency,
  defaultCurrency = catalog.currency.options[0]?.id ?? "",
  defaultLanguage = catalog.language.options[0]?.id ?? "",
  language,
  onAction,
  onCurrencyChange,
  onLanguageChange,
  onSubmenuChange,
}: MerrySystemMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const [requestedSubmenu, setRequestedSubmenu] =
    useState<MerrySystemMenuSubmenuView | null>(null);
  const [renderedSubmenu, setRenderedSubmenu] =
    useState<MerrySystemMenuSubmenuView | null>(null);
  const [internalCurrency, setInternalCurrency] = useState(defaultCurrency);
  const [internalLanguage, setInternalLanguage] = useState(defaultLanguage);
  const [isAnimating, setIsAnimating] = useState(false);
  const activeCurrency = currency ?? internalCurrency;
  const activeLanguage = language ?? internalLanguage;

  const changeSubmenu = useCallback(
    (nextSubmenu: MerrySystemMenuSubmenuView | null) => {
      setRequestedSubmenu(nextSubmenu);
      onSubmenuChange?.(nextSubmenu);
    },
    [onSubmenuChange],
  );

  const handleViewChange = useCallback(
    (nextView: MerryCardMenuView) => {
      if (!isMerrySystemMenuSubmenuView(nextView)) return;
      changeSubmenu(requestedSubmenu === nextView ? null : nextView);
    },
    [changeSubmenu, requestedSubmenu],
  );

  const updateCurrency = useCallback(
    (nextCurrency: string) => {
      if (currency === undefined) setInternalCurrency(nextCurrency);
      onCurrencyChange?.(nextCurrency);
    },
    [currency, onCurrencyChange],
  );

  const updateLanguage = useCallback(
    (nextLanguage: string) => {
      if (language === undefined) setInternalLanguage(nextLanguage);
      onLanguageChange?.(nextLanguage);
    },
    [language, onLanguageChange],
  );

  useGSAP(
    () => {
      const flyout = flyoutRef.current;

      if (requestedSubmenu === renderedSubmenu) {
        if (flyout) {
          gsap.killTweensOf(flyout);
          gsap.set(flyout, {
            autoAlpha: 1,
            clearProps: "opacity,transform,visibility",
          });
        }
        setIsAnimating(false);
        return;
      }

      if (prefersReducedMotion()) {
        setRenderedSubmenu(requestedSubmenu);
        setIsAnimating(false);
        return;
      }

      setIsAnimating(true);
      if (!flyout) {
        setRenderedSubmenu(requestedSubmenu);
        return;
      }

      gsap.killTweensOf(flyout);
      gsap.to(flyout, {
        autoAlpha: 0,
        duration: 0.12,
        ease: "power2.in",
        overwrite: true,
        x: 12,
        onComplete: () => setRenderedSubmenu(requestedSubmenu),
      });
    },
    {
      dependencies: [requestedSubmenu],
      scope: rootRef,
    },
  );

  useGSAP(
    () => {
      const flyout = flyoutRef.current;
      if (!renderedSubmenu || !flyout) {
        setIsAnimating(false);
        return;
      }

      gsap.killTweensOf(flyout);
      if (prefersReducedMotion()) {
        gsap.set(flyout, {
          autoAlpha: 1,
          clearProps: "opacity,transform,visibility",
        });
        setIsAnimating(false);
        return;
      }

      setIsAnimating(true);
      gsap.fromTo(
        flyout,
        { autoAlpha: 0, x: 12 },
        {
          autoAlpha: 1,
          duration: 0.22,
          ease: "power3.out",
          overwrite: true,
          x: 0,
          onComplete: () => {
            gsap.set(flyout, {
              clearProps: "opacity,transform,visibility",
            });
            setIsAnimating(false);
          },
        },
      );
    },
    {
      dependencies: [renderedSubmenu],
      scope: rootRef,
    },
  );

  const page = requestedSubmenu ?? "account";

  return (
    <div
      aria-label={ariaLabel}
      className={["merry-system-menu", className].filter(Boolean).join(" ")}
      data-figma-node-id={getMerrySystemMenuPageFigmaNodeId(page)}
      data-system-menu-animating={isAnimating ? "true" : "false"}
      data-system-menu-submenu={requestedSubmenu ?? "none"}
      data-testid={testId}
      ref={rootRef}
      role="group"
    >
      {renderedSubmenu ? (
        <div
          className="merry-system-menu__flyout"
          data-system-menu-flyout={renderedSubmenu}
          data-testid={testId ? `${testId}-flyout` : undefined}
          ref={flyoutRef}
          style={{ top: getMerrySystemMenuFlyoutTop(renderedSubmenu) }}
        >
          <MerryCardMenu
            aria-label={`${catalog[renderedSubmenu].title}二级菜单`}
            animateEntrance={false}
            catalog={catalog}
            currency={activeCurrency}
            language={activeLanguage}
            onAction={onAction}
            onCurrencyChange={updateCurrency}
            onLanguageChange={updateLanguage}
            view={renderedSubmenu}
          />
        </div>
      ) : null}

      <MerryCardMenu
        activeAccountView={requestedSubmenu}
        aria-label="账户设置主菜单"
        catalog={catalog}
        className="merry-system-menu__primary"
        currency={activeCurrency}
        data-testid={testId ? `${testId}-primary` : undefined}
        language={activeLanguage}
        onAction={onAction}
        onViewChange={handleViewChange}
        view="account"
      />
    </div>
  );
}
